import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode, useRef } from 'react';
import { useAuth, useUser } from '@clerk/clerk-react';
import { useSearchParams, useLocation } from 'react-router-dom';
import { UserRole } from '../constants/roles';
import { createAuthHeaders } from '../utils/authHeaders';
import { setSentryUser, clearSentryUser, addSentryBreadcrumb } from '../utils/sentry';
import { clearRecreateStorage, clearForeignRecreateStorage } from '../utils/recreateStorage';

interface Organization {
  id: number;
  name: string;
  joinedAt: string;
  role?: UserRole | null; // Role in this specific organization
  margin_discount?: number; // Per-org discount applied to margin
  payment_method?: 'none' | 'standard' | 'enterprise'; // Payment method for the organization
  usage?: {
    limit: number;
    used: number;
    remaining: number;
    isCustom?: boolean;
  };
}

interface UserAccess {
  userId: string;
  userEmail: string;
  isAdmin: boolean;
  hasAccess: boolean;
  organizations: Organization[];
  currentRole?: UserRole;
}

interface OrganizationContextType {
  // User access info
  userAccess: UserAccess | null;
  isLoading: boolean;
  
  // Current organization
  currentOrganization: Organization | null;
  
  // Actions
  checkUserAccess: (userId: string, userEmail: string) => Promise<void>;
  switchOrganization: (organizationId: number) => Promise<void>;
  refreshOrganizations: () => Promise<void>;
  leaveCurrentOrganization: (organizationId: number) => Promise<void>;
  clearAccess: () => void;
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

export const useOrganization = () => {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error('useOrganization must be used within an OrganizationProvider');
  }
  return context;
};

/**
 * Same context, but tolerates the provider being absent (returns `undefined`).
 *
 * For providers that sit INSIDE `OrganizationProvider` in the app but are
 * rendered standalone in tests — `WidgetProvider` needs the active organization
 * to drop the previous tenant's staged input media on a switch, and must not
 * start throwing in every test that mounts it on its own.
 */
export const useOptionalOrganization = (): OrganizationContextType | undefined =>
  useContext(OrganizationContext);

interface OrganizationProviderProps {
  children: ReactNode;
}

export const OrganizationProvider: React.FC<OrganizationProviderProps> = ({ children }) => {
  const [userAccess, setUserAccess] = useState<UserAccess | null>(null);
  const [currentOrganization, setCurrentOrganization] = useState<Organization | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const adminOrgLoadInFlightRef = useRef(false);

  
  const { getToken, userId, isLoaded } = useAuth();
  const { user, isLoaded: userIsLoaded } = useUser();
  
  // Safe router hooks usage - fallback to window.location if router not available
  let searchParams: URLSearchParams | null = null;
  let setSearchParams: ((params: URLSearchParams | ((prev: URLSearchParams) => URLSearchParams)) => void) | null = null;

  try {
    const [params, setParams] = useSearchParams();
    searchParams = params;
    setSearchParams = setParams;
  } catch (error) {
    // Router not available, use window.location as fallback
    searchParams = new URLSearchParams(window.location.search);
    setSearchParams = null;
  }

  // Helper to update URL params - uses React Router if available, falls back to window.history
  const updateSearchParams = useCallback((updater: (params: URLSearchParams) => void) => {
    if (setSearchParams) {
      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev);
        updater(newParams);
        return newParams;
      });
    } else {
      // Fallback for when router is not available
      const newParams = new URLSearchParams(window.location.search);
      updater(newParams);
      const newUrl = window.location.pathname +
        (newParams.toString() ? `?${newParams.toString()}` : '');
      window.history.pushState({}, '', newUrl);
    }
  }, [setSearchParams]);

  const orgIdFromUrl = searchParams?.get('orgId') || null;

  // Drop the gallery→workbench "recreate" hand-off whenever the tenant changes.
  //
  // Those sessionStorage blobs carry one organization's raw inputs (media URLs,
  // reference bodies, prompt, widget values) and are picked up by widgets only
  // after they mount and finish their own async loads, so they outlive the
  // navigation and a switch in between would hydrate the next tenant's BILLED
  // generation with the previous one's data. The org-aware consumers also check
  // the per-blob stamp themselves (they can read in the same commit as the
  // switch, before this effect); this purge is what covers the media widgets,
  // which read a bare URL array and have no organization context of their own.
  // See `src/utils/recreateStorage.ts`.
  //
  // Guarded by a ref rather than a bare dep compare: `currentOrganization`
  // legitimately blips through null while access refreshes, and purging on
  // `5 → null → 5` would break a recreate the user is in the middle of. The
  // initial null → A transition is not a switch either, so it must not purge
  // wholesale — that is the commit the blob was written for.
  //
  // It cannot be skipped outright either. This ref lives in memory while the
  // blobs live in sessionStorage, so a reload (F5, or the automatic
  // `lazyWithRetry`/`ChunkErrorBoundary` reload after a deploy) resets the ref
  // but not the blobs — and the organization we resolve to comes from
  // `localStorage.currentOrganizationId`, which is SHARED ACROSS TABS. A switch
  // made in another tab therefore arrives here as an "initial" value, so org A's
  // media URLs would survive into an org B session with nothing to catch them.
  // On that transition we drop exactly the blobs stamped for someone else.
  const recreateOrgRef = useRef<number | null>(null);
  useEffect(() => {
    const orgId = currentOrganization?.id;
    if (!orgId) return;
    const storage = typeof window !== 'undefined' ? window.sessionStorage : null;
    if (recreateOrgRef.current === null) {
      clearForeignRecreateStorage(storage, orgId);
    } else if (recreateOrgRef.current !== orgId) {
      clearRecreateStorage(storage);
    }
    recreateOrgRef.current = orgId;
  }, [currentOrganization?.id]);

  // Auto-trigger checkUserAccess when Clerk is loaded and we have user but no userAccess
  useEffect(() => {
    const bothLoaded = isLoaded && userIsLoaded;
    const userEmail = user?.emailAddresses?.[0]?.emailAddress;
    
    
    if (bothLoaded) {
      if (userId && userEmail && !userAccess) {
        // We have both user ID and email but no access data - auto-check access
        checkUserAccess(userId, userEmail);
      } else if (!userId) {
        // No user ID - clear everything and stop loading
        setUserAccess(null);
        setCurrentOrganization(null);
        clearSentryUser();
        addSentryBreadcrumb('auth', 'User signed out', 'info');
        setIsLoading(false);
      } else if (userAccess) {
        // We have userAccess, no need to be loading
        setIsLoading(false);
      }
    } else {
      // Clerk is still loading
      setIsLoading(true);
    }
  }, [isLoaded, userIsLoaded, userId, user?.emailAddresses, userAccess]);
  
  // Вспомогательная функция для создания заголовков с JWT токеном
  const createAuthHeadersCallback = useCallback(async (additionalHeaders?: HeadersInit): Promise<HeadersInit> => {
    return createAuthHeaders(getToken, additionalHeaders);
  }, [getToken]);

  // Special logic for global admins using URL orgId parameter
  useEffect(() => {
    // Helper function to fall back to default organization
    const fallbackToDefaultOrg = () => {
      const savedOrgId = localStorage.getItem('currentOrganizationId');
      if (savedOrgId && userAccess?.organizations) {
        const savedOrg = userAccess.organizations.find(org => String(org.id) === String(savedOrgId));
        if (savedOrg) {
          setCurrentOrganization(savedOrg);
        } else if (userAccess.organizations.length > 0) {
          // If saved org not found, set first available
          setCurrentOrganization(userAccess.organizations[0]);
          localStorage.setItem('currentOrganizationId', String(userAccess.organizations[0].id));
        }
      }
    };

    if (orgIdFromUrl && userAccess?.isAdmin) {
      const currentOrgMatches = currentOrganization?.id
        && String(currentOrganization.id) === String(orgIdFromUrl);
      if (currentOrgMatches) {
        return;
      }

      // For global admins, prioritize URL orgId over saved preferences
      // First, try to find in user's organizations list
      const targetOrg = userAccess.organizations?.find(org => String(org.id) === String(orgIdFromUrl));

      if (targetOrg) {
        setCurrentOrganization(targetOrg);
        // Don't save to localStorage for global admin URL navigation
        return; // Skip the normal localStorage logic below
      }

      // Organization not found in user's list - load via admin API
      const loadAdminTargetOrg = async () => {
        if (adminOrgLoadInFlightRef.current) {
          return;
        }
        adminOrgLoadInFlightRef.current = true;
        let orgLoaded = false;
        try {
          const token = await getToken({ template: 'withmail' });
          const response = await fetch('/api/admin/organizations', {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });

          if (response.ok) {
            const data = await response.json();
            const adminTargetOrg = data.organizations?.find(
              (org: any) => String(org.id) === String(orgIdFromUrl)
            );

            if (adminTargetOrg) {
              // Create organization object for context
              setCurrentOrganization({
                id: adminTargetOrg.id,
                name: adminTargetOrg.name,
                joinedAt: adminTargetOrg.created_at || new Date().toISOString(),
                role: null, // Admin is not a member
                margin_discount: adminTargetOrg.margin_discount ?? 0,
                payment_method: adminTargetOrg.payment_method ?? 'none',
                usage: adminTargetOrg.usage || { limit: 0, used: 0, remaining: 0 }
              });
              orgLoaded = true;
              console.log('🌟 Admin loaded non-member organization via API:', adminTargetOrg.id, adminTargetOrg.name);
              return;
            }
          }
        } catch (error) {
          console.error('Error loading admin target organization:', error);
        } finally {
          if (!orgLoaded) {
            // If loading failed or org not found, fall back to default org
            fallbackToDefaultOrg();
          }
          adminOrgLoadInFlightRef.current = false;
        }
      };

      loadAdminTargetOrg();
      return;
    }

    // Normal user logic: Load saved organization from localStorage
    adminOrgLoadInFlightRef.current = false;
    fallbackToDefaultOrg();
  }, [userAccess, orgIdFromUrl, getToken, currentOrganization?.id]);

  const checkUserAccess = useCallback(async (userId: string, userEmail: string) => {
    if (!userId || !userEmail) {
      setUserAccess(null);
      setCurrentOrganization(null);
      setIsLoading(false);
      return;
    }
    
    setIsLoading(true);
    try {
      // Используем только JWT токен для авторизации
      const headers = await createAuthHeadersCallback();
      
      const response = await fetch('/api/auth/check-access', {
        method: 'POST',
        headers,
      });

      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: any = await response.json();

      console.log('📥 check-access API response:', data);
      console.log('📥 Organizations with usage:', data.organizations);

      const accessInfo: UserAccess = {
        userId: data.userId,
        userEmail: data.userEmail,
        isAdmin: data.isAdmin,
        hasAccess: data.hasAccess,
        organizations: data.organizations || [],
        currentRole: data.currentRole || data.role || (data.isAdmin ? 'Admin' : 'User')
      };

      setUserAccess(accessInfo);
      setSentryUser({ id: accessInfo.userId, email: accessInfo.userEmail });
      addSentryBreadcrumb('auth', 'User signed in', 'info', { userId: accessInfo.userId });

      // Set current organization if user has organizations
      if (accessInfo.organizations.length > 0) {
        const savedOrgId = localStorage.getItem('currentOrganizationId');
        const savedOrgIdNum = savedOrgId ? parseInt(savedOrgId, 10) : null;
        const savedOrg = accessInfo.organizations.find(org => org.id === savedOrgIdNum);
        const orgToSet = savedOrg || accessInfo.organizations[0];

        console.log('✅ Setting currentOrganization:', orgToSet);
        setCurrentOrganization(orgToSet);
        localStorage.setItem('currentOrganizationId', orgToSet.id.toString());
        
        // Set currentRole based on the role in the current organization
        if (orgToSet.role && !data.isAdmin) {
          setUserAccess(prev => prev ? { ...prev, currentRole: orgToSet.role as UserRole } : prev);
        }
      }

    } catch (error) {
      console.error('❌ OrganizationContext: Error checking user access:', error);
      setUserAccess(null);
      setCurrentOrganization(null);
    } finally {
      console.log('✅ OrganizationContext: Finished checkUserAccess, setting isLoading to false');
      setIsLoading(false);
    }
  }, [createAuthHeadersCallback]);

  const switchOrganization = async (organizationId: number) => {
    if (!userAccess) return;

    try {
      const headers = await createAuthHeadersCallback();

      const response = await fetch('/api/organizations/switch', {
        method: 'POST',
        headers,
        body: JSON.stringify({ organizationId }),
      });

      if (!response.ok) {
        // If 403 and user is admin, try admin mode with URL parameter
        if (response.status === 403 && userAccess.isAdmin) {
          console.log('🌟 Admin access denied for normal switch, using admin mode with URL parameter:', organizationId);

          // Use React Router's setSearchParams to properly sync URL state
          updateSearchParams(params => params.set('orgId', organizationId.toString()));

          // First try to find in user's organizations list
          let selectedOrg = userAccess.organizations.find(org => org.id === organizationId);

          // If not found in user's list, load via admin API
          if (!selectedOrg) {
            try {
              const token = await getToken({ template: 'withmail' });
              const adminResponse = await fetch('/api/admin/organizations', {
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json',
                },
              });

              if (adminResponse.ok) {
                const data = await adminResponse.json();
                const adminTargetOrg = data.organizations?.find(
                  (org: any) => org.id === organizationId
                );

                if (adminTargetOrg) {
                  // Create organization object
                  selectedOrg = {
                    id: adminTargetOrg.id,
                    name: adminTargetOrg.name,
                    joinedAt: adminTargetOrg.created_at || new Date().toISOString(),
                    role: null,
                    margin_discount: adminTargetOrg.margin_discount ?? 0,
                    payment_method: adminTargetOrg.payment_method ?? 'none',
                    usage: adminTargetOrg.usage || { limit: 0, used: 0, remaining: 0 }
                  };
                  console.log('🌟 Admin loaded non-member organization for switch:', adminTargetOrg.id, adminTargetOrg.name);
                }
              }
            } catch (error) {
              console.error('Error loading admin target organization for switch:', error);
            }
          }

          if (selectedOrg) {
            setCurrentOrganization(selectedOrg);
            localStorage.setItem('currentOrganizationId', organizationId.toString());

            // For global admins switching to an organization, update currentRole
            setUserAccess(prev => {
              if (!prev) return prev;
              return {
                ...prev,
                currentRole: selectedOrg!.role || (prev.isAdmin ? 'Admin' : 'User')
              };
            });

          }

          return; // Skip normal error handling for admin
        }

        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: any = await response.json();
      
      if (data.success && data.organization) {
        // Clear any orgId parameter when switching to member organization successfully
        // Use React Router's setSearchParams to properly sync URL state
        if (searchParams?.has('orgId')) {
          updateSearchParams(params => params.delete('orgId'));
        }
        
        // Find the original organization to preserve its role
        const originalOrg = userAccess.organizations.find(org => org.id === organizationId);
        
        const updatedOrg: Organization = {
          id: data.organization.id,
          name: data.organization.name,
          joinedAt: originalOrg?.joinedAt || '',
          role: originalOrg?.role || null, // Preserve the role
          margin_discount: data.organization.margin_discount ?? originalOrg?.margin_discount ?? 0,
          payment_method: data.organization.payment_method ?? originalOrg?.payment_method ?? 'none',
          usage: data.organization.usage
        };

        setCurrentOrganization(updatedOrg);
        localStorage.setItem('currentOrganizationId', organizationId.toString());

        // Update the organization in the user's organizations list and update currentRole
        setUserAccess(prev => {
          if (!prev) return prev;

          // For global admins, preserve their admin status
          // For regular users, use the organization-specific role
          const newRole = prev.isAdmin ? 'Admin' : (updatedOrg.role || 'User');

          console.log('🔄 Switching org - updatedOrg.role:', updatedOrg.role, 'isAdmin:', prev.isAdmin, 'newRole:', newRole);

          return {
            ...prev,
            currentRole: newRole,
            organizations: prev.organizations.map(org =>
              org.id === organizationId ? updatedOrg : org
            )
          };
        });
      }
    } catch (error) {
      console.error('Error switching organization:', error);
    }
  };

  const refreshOrganizations = async () => {
    if (!userAccess) return;

    try {
      const headers = await createAuthHeadersCallback();
      
      const response = await fetch('/api/organizations', {
        headers,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: any = await response.json();
      
      setUserAccess(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          organizations: data.organizations
        };
      });

      // Update current organization if it's in the list
      if (currentOrganization) {
        const updatedCurrentOrg = data.organizations.find(
          (org: Organization) => org.id === currentOrganization.id
        );
        if (updatedCurrentOrg) {
          setCurrentOrganization(updatedCurrentOrg);
        }
      }
    } catch (error) {
      console.error('Error refreshing organizations:', error);
    }
  };

  const leaveCurrentOrganization = async (organizationId: number) => {
    try {
      const headers = await createAuthHeadersCallback();
      
      const response = await fetch('/api/organizations/leave', {
        method: 'POST',
        headers,
        body: JSON.stringify({ organizationId }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to leave organization');
      }

      // Remove the organization from user's organizations
      setUserAccess(prev => {
        if (!prev) return prev;
        const updatedOrganizations = prev.organizations.filter(org => org.id !== organizationId);
        return {
          ...prev,
          organizations: updatedOrganizations
        };
      });

      // If leaving current organization, switch to another one or clear
      if (currentOrganization?.id === organizationId) {
        const remainingOrgs = userAccess?.organizations.filter(org => org.id !== organizationId) || [];
        if (remainingOrgs.length > 0) {
          // Switch to the first remaining organization
          setCurrentOrganization(remainingOrgs[0]);
          localStorage.setItem('currentOrganizationId', remainingOrgs[0].id.toString());
        } else {
          // No organizations left
          setCurrentOrganization(null);
          localStorage.removeItem('currentOrganizationId');
        }
      }

    } catch (error) {
      console.error('Error leaving organization:', error);
      throw error; // Re-throw to allow component to handle the error
    }
  };

  const clearAccess = () => {
    setUserAccess(null);
    setCurrentOrganization(null);
    clearSentryUser();
    addSentryBreadcrumb('auth', 'User signed out', 'info');
    localStorage.removeItem('currentOrganizationId');
  };

  const contextValue: OrganizationContextType = {
    userAccess,
    isLoading,
    currentOrganization,
    checkUserAccess,
    switchOrganization,
    refreshOrganizations,
    leaveCurrentOrganization,
    clearAccess,
  };

  return (
    <OrganizationContext.Provider value={contextValue}>
      {children}
    </OrganizationContext.Provider>
  );
};
